// Playback engine (docs/04 §Playback) — double-buffered crossfade, RAF-driven.
//
// Two stacked full-screen layers. The NEXT item is prepared (built + preloaded)
// in the hidden layer while the current one plays, then swapped in with an
// opacity crossfade — never a white flash. Teardown of an outgoing element
// happens when its (fully faded-out) layer is reused to prepare a later item, so
// the double buffer is never clobbered and hidden videos don't keep decoding.
//
// The loop is requestAnimationFrame-based: monotonic `performance.now()` drives
// item durations, wall-clock `Date.now()` drives window boundaries — so window
// transitions happen with NO network. Failures are contained: a thrown frame
// never kills the loop; a wedged <video> is skipped and never reused.
//
// Impure services are injected (docs/10 §2.4): media resolution via MediaSource,
// telemetry via PlayerLogger. The engine itself touches no storage, network,
// service worker, or `window.location`.

import type { MediaSource, PlayerLogger } from './ports.ts';
import type { Manifest, ManifestItem } from './types.ts';
import { activeItems, activeSetKey } from './timeline.ts';
import { OverlayLayer } from './overlay.ts';

// Crossfade duration lives in CSS (.layer transition, styles.css). Item timing
// below is independent of it — the fade just visually overlaps two items.
const IDLE_RECHECK_MS = 1000;
const READY_TIMEOUT_MS = 5000; // don't stall forever waiting for a slow element
const VIDEO_STALL_SKIP_MS = 10000;

interface Prepared {
  item: ManifestItem;
  el: HTMLElement;
  ready: Promise<void>;
}

/**
 * Optional engine hooks. Absent (CMS preview) = today's behavior exactly — no
 * emission, no observable change. The player passes `onItemPlayed` to collect
 * playback analytics (apps/player).
 */
export interface PlaybackConfig {
  /**
   * Fired when the item currently on screen leaves it — a normal advance, a skip
   * (video error/stall/natural end), or a manifest swap that drops it — reporting
   * the ACTUAL milliseconds it was visible. Never called for an item still
   * playing. Must not throw; the engine catches and logs callback errors so
   * telemetry can never take down playback.
   */
  onItemPlayed?: (item: ManifestItem, playedMs: number) => void;
}

export class PlaybackEngine {
  private layers: [HTMLDivElement, HTMLDivElement];
  private front = 0; // index of the currently visible layer
  private overlay: OverlayLayer;
  private manifest: Manifest | null = null;

  private setKey = '';
  private index = 0;
  private currentItemId: string | null = null;
  // Full item + its on-screen start (performance.now()) — used to report the
  // actual played duration when it leaves the screen. Preserved across a manifest
  // swap that keeps the item, so its reported duration spans the swap.
  private currentItem: ManifestItem | null = null;
  private currentStartedAt = 0;
  private endsAt = 0; // performance.now() deadline for the current item
  private advancing = false;
  private prepared: Prepared | null = null;
  private idleShown = false;

  private raf = 0;
  private lastFrameAt = performance.now();
  private tick = 0;

  constructor(
    private root: HTMLElement,
    private media: MediaSource,
    private log: PlayerLogger,
    private config: PlaybackConfig = {},
  ) {
    this.root.classList.add('stage');
    this.layers = [document.createElement('div'), document.createElement('div')];
    for (const l of this.layers) {
      l.className = 'layer';
      l.style.opacity = '0';
      this.root.appendChild(l);
    }
    this.layers[0].style.opacity = '1';
    this.renderIdle(this.layers[0]);
    this.idleShown = true;
    // Overlay is appended AFTER the two media layers → it stacks on top and is
    // untouched by the crossfade.
    this.overlay = new OverlayLayer(this.root, this.media);
  }

  /**
   * Atomically switch to a new manifest (caller guarantees its media is
   * loadable). Playback is preserved where possible: if the item currently on
   * screen still exists in the new active set, it keeps playing for its
   * remaining duration and the new sequence resumes from the item that follows
   * it — no jarring cut. Only when the current item is gone (or nothing is
   * playing) does the engine advance into the new set immediately.
   */
  setManifest(m: Manifest): void {
    const now = Date.now();
    const items = activeItems(m, now);
    const keepAt =
      !this.idleShown && this.currentItemId !== null
        ? items.findIndex((it) => it.id === this.currentItemId)
        : -1;

    this.manifest = m;
    // Overlay swaps atomically with the manifest; a no-op when unchanged.
    this.overlay.apply(m.overlay ?? null);
    // Adopt the new set's identity so `step()` doesn't treat this as a window
    // boundary (which would reset to item 0); drop the now-stale prefetch.
    this.setKey = activeSetKey(m, now);
    this.prepared = null;

    if (keepAt >= 0) {
      // Keep the current item on screen; play the rest of the new sequence next.
      this.index = keepAt + 1;
      // endsAt is left untouched → the current item finishes naturally.
    } else {
      this.index = 0;
      this.endsAt = 0; // re-evaluate (advance into the new set) on the next frame
    }
  }

  start(): void {
    if (this.raf) return;
    const frame = () => {
      this.lastFrameAt = performance.now();
      this.tick++;
      try {
        this.step();
      } catch (e) {
        this.log.add('error', 'frame_error', String(e));
      }
      this.raf = requestAnimationFrame(frame);
    };
    this.raf = requestAnimationFrame(frame);
  }

  stop(): void {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  // Watchdog + heartbeat probes.
  msSinceFrame(): number {
    return performance.now() - this.lastFrameAt;
  }
  getTick(): number {
    return this.tick;
  }
  getCurrentItemId(): string | null {
    return this.currentItemId;
  }

  /**
   * Test seam (?mock=1): backdate the frame heartbeat so the watchdog observes a
   * freeze on its very next check. Runs synchronously so no real RAF frame
   * resets the clock before the watchdog reads it.
   */
  forceFrozenForTest(): void {
    this.lastFrameAt = performance.now() - 60_000;
  }

  private step(): void {
    if (!this.manifest || this.advancing) return;
    const now = Date.now();
    const key = activeSetKey(this.manifest, now);
    // Window boundary crossed → restart the new set from item 0.
    if (key !== this.setKey) {
      this.setKey = key;
      this.index = 0;
      this.endsAt = 0;
      this.prepared = null;
    }
    if (performance.now() >= this.endsAt) void this.advance();
  }

  private async advance(): Promise<void> {
    if (!this.manifest) return;
    this.advancing = true;
    this.endsAt = Infinity; // hold until the transition completes
    try {
      const now = Date.now();
      const items = activeItems(this.manifest, now);
      if (items.length === 0) {
        await this.showIdle();
        return;
      }
      const item = items[this.index % items.length]!;
      this.index++;

      let next = this.prepared;
      this.prepared = null;
      if (!next || next.item.id !== item.id) {
        next = await this.prepare(item);
      }
      if (!next) {
        // Media missing from the source — skip to the next item promptly.
        this.log.add('warn', 'cache_miss', item.url, item.id);
        this.endsAt = 0;
        return;
      }
      await this.raceReady(next.ready);
      // The outgoing item's window ends the instant the new one is swapped in.
      const swapAt = performance.now();
      this.reportPlayed(swapAt);
      this.crossfade();
      this.currentItem = item;
      this.currentItemId = item.id;
      this.currentStartedAt = swapAt;
      this.idleShown = false;
      this.armDuration(next);
      // Prefetch the following item into the now-hidden layer.
      void this.prefetchNext(items);
    } finally {
      this.advancing = false;
    }
  }

  /**
   * Emit `onItemPlayed` for the item leaving the screen. Opt-in: a no-op unless a
   * callback was configured (CMS previews pass none). `now` is the same
   * performance.now() reading used to start the incoming item, so played spans
   * line up exactly.
   */
  private reportPlayed(now: number): void {
    const cb = this.config.onItemPlayed;
    if (!cb || !this.currentItem) return;
    const playedMs = Math.max(0, Math.round(now - this.currentStartedAt));
    try {
      cb(this.currentItem, playedMs);
    } catch (e) {
      this.log.add('error', 'item_played_cb_error', String(e));
    }
  }

  private armDuration(p: Prepared): void {
    this.endsAt = performance.now() + p.item.duration_ms;
    if (p.item.type === 'video') {
      const v = p.el as HTMLVideoElement;
      v.play().catch(() => this.log.add('warn', 'video_play_blocked', undefined, p.item.id));
      // Natural end vs duration_ms override — whichever comes first wins.
      v.addEventListener('ended', () => (this.endsAt = 0), { once: true });
      this.attachVideoHealing(v, p.item.id);
    }
  }

  private attachVideoHealing(v: HTMLVideoElement, itemId: string): void {
    let stallTimer: ReturnType<typeof setTimeout> | null = null;
    const clearStall = () => {
      if (stallTimer) clearTimeout(stallTimer);
      stallTimer = null;
    };
    v.addEventListener('error', () => {
      this.log.add('error', 'video_error', undefined, itemId);
      clearStall();
      this.endsAt = 0; // skip now; a fresh element is built for the next item
    });
    v.addEventListener('stalled', () => {
      if (stallTimer) return;
      stallTimer = setTimeout(() => {
        this.log.add('warn', 'video_stalled_skip', undefined, itemId);
        this.endsAt = 0;
      }, VIDEO_STALL_SKIP_MS);
    });
    v.addEventListener('playing', clearStall);
    v.addEventListener('timeupdate', clearStall);
  }

  private async prefetchNext(items: ManifestItem[]): Promise<void> {
    if (items.length < 2) {
      this.prepared = null;
      return;
    }
    const nextItem = items[this.index % items.length]!;
    this.prepared = await this.prepare(nextItem);
  }

  /**
   * Build the element for `item` into the hidden back layer and start loading.
   * Tears down whatever that (fully faded-out) layer held first, so a hidden
   * video stops decoding and its memory is released.
   */
  private async prepare(item: ManifestItem): Promise<Prepared | null> {
    // Delivery per MANIFEST_ITEM_DELIVERY (@seencast/shared): 'url' is a LIVE external
    // page, used as-is; every other type ('image'/'video'/'html') goes through the media
    // source, which serves it from cache as a blob object-URL. That split is what lets an
    // 'html' item — whose url is the device-token-authed /v1/player/media/:id — render at
    // all: an <iframe> cannot send an Authorization header, so a live src would 401 blank.
    const src = item.type === 'url' ? item.url : await this.media.resolveSrc(item.url);
    if (src === null) return null;
    const back = this.layers[this.front ^ 1]!;
    this.teardownLayer(back);
    const el = this.buildElement(item, src);
    back.appendChild(el);
    return { item, el, ready: this.elementReady(el, item) };
  }

  private teardownLayer(layer: HTMLElement): void {
    const v = layer.querySelector('video');
    if (v) {
      v.pause();
      v.removeAttribute('src');
      v.load();
    }
    layer.replaceChildren();
  }

  private buildElement(item: ManifestItem, src: string): HTMLElement {
    const objectFit = item.fit === 'stretch' ? 'fill' : item.fit;
    if (item.type === 'image') {
      const img = document.createElement('img');
      img.className = 'media';
      img.style.objectFit = objectFit;
      img.decoding = 'async';
      img.src = src;
      return img;
    }
    if (item.type === 'video') {
      const v = document.createElement('video');
      v.className = 'media';
      v.style.objectFit = objectFit;
      v.muted = true;
      v.autoplay = true;
      v.playsInline = true;
      v.preload = 'auto';
      v.src = src;
      return v;
    }
    // type === 'url' | 'html' — rendered in a sandboxed iframe (docs/09 §B). 'url' is
    // a live external page (src = the URL as-is); 'html' is a generated self-contained
    // document already in the media cache (src = the blob object-URL prepare() resolved).
    // Both use `sandbox="allow-scripts"` with NO allow-same-origin, so generated HTML
    // runs isolated with no network and no access to the player origin.
    const iframe = document.createElement('iframe');
    iframe.className = 'media';
    iframe.setAttribute('sandbox', 'allow-scripts');
    iframe.setAttribute('referrerpolicy', 'no-referrer');
    iframe.src = src;
    return iframe;
  }

  private elementReady(el: HTMLElement, item: ManifestItem): Promise<void> {
    return new Promise((resolve) => {
      if (item.type === 'image') {
        const img = el as HTMLImageElement;
        if (img.complete && img.naturalWidth > 0) return resolve();
        img.addEventListener('load', () => resolve(), { once: true });
        img.addEventListener('error', () => resolve(), { once: true });
      } else if (item.type === 'video') {
        const v = el as HTMLVideoElement;
        if (v.readyState >= 2) return resolve();
        v.addEventListener('loadeddata', () => resolve(), { once: true });
        v.addEventListener('error', () => resolve(), { once: true });
      } else {
        el.addEventListener('load', () => resolve(), { once: true });
      }
    });
  }

  /** Wait for readiness but never longer than READY_TIMEOUT_MS (avoid a stall). */
  private raceReady(ready: Promise<void>): Promise<void> {
    return Promise.race([ready, new Promise<void>((r) => setTimeout(r, READY_TIMEOUT_MS))]);
  }

  /** Swap the back (already-populated) layer in front of the current one. */
  private crossfade(): void {
    const back = this.layers[this.front ^ 1]!;
    const old = this.layers[this.front]!;
    back.style.opacity = '1';
    old.style.opacity = '0';
    this.front ^= 1;
  }

  private async showIdle(): Promise<void> {
    if (!this.idleShown) {
      const back = this.layers[this.front ^ 1]!;
      this.teardownLayer(back);
      this.renderIdle(back);
      this.reportPlayed(performance.now()); // the item just left the screen for idle
      this.crossfade();
      this.idleShown = true;
      this.currentItem = null;
      this.currentItemId = null;
    }
    this.endsAt = performance.now() + IDLE_RECHECK_MS;
  }

  /** Black screen with a subtle centered logo placeholder (docs/04 no-fallback case). */
  private renderIdle(layer: HTMLElement): void {
    layer.replaceChildren();
    const badge = document.createElement('div');
    badge.className = 'idle-logo';
    badge.textContent = 'SEENCAST';
    layer.appendChild(badge);
  }
}
